import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const REQUIRED_HEADERS = [
  'patient_id',
  'first_name',
  'last_name',
  'date_of_birth',
] as const;

export const OPTIONAL_HEADERS = ['email', 'phone_number'] as const;

export type RequiredHeader = (typeof REQUIRED_HEADERS)[number];
export type OptionalHeader = (typeof OPTIONAL_HEADERS)[number];

// Status enum values must match DB enum-table values verbatim.

export const PATIENT_IMPORT_KNOWN_SOURCES = [
  { sourceKey: 'JANE', displayName: 'Jane' },
  { sourceKey: 'TEBRA', displayName: 'Tebra' },
  { sourceKey: 'MOVEMENT_X', displayName: 'MovementX' },
  { sourceKey: 'BLVD', displayName: 'Boulevard' },
] as const;

export const patientImportKnownSourceKeySchema = z.enum([
  'JANE',
  'TEBRA',
  'MOVEMENT_X',
  'BLVD',
]);
export type PatientImportKnownSourceKey = z.infer<
  typeof patientImportKnownSourceKeySchema
>;

const patientImportKnownSourceSchema = z.object({
  sourceKey: patientImportKnownSourceKeySchema,
  displayName: z.string(),
});

const patientImportSourceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  sourceKey: z.string(),
  displayName: z.string(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  archivedAt: z.string().nullable(),
});
export type PatientImportSource = z.infer<typeof patientImportSourceSchema>;

export const patientImportJobStatusSchema = z.enum([
  'PARSING',
  'HEADER_MISMATCH',
  'VALIDATING',
  'REVIEW_READY',
  'COMMITTING',
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'CANCELLED',
  'FAILED',
]);
export type PatientImportJobStatus = z.infer<
  typeof patientImportJobStatusSchema
>;

export const patientImportRowDedupeStatusSchema = z.enum([
  'PENDING',
  'NEW',
  'DUPE_ID_EXACT_MATCH',
  'DUPE_ID_SAME_PATIENT',
  'DUPE_ID_DIFFERENT_PATIENT',
  'DUPE_IN_FILE',
  'PATIENT_EXISTS_NO_ID_MATCH',
]);
export type PatientImportRowDedupeStatus = z.infer<
  typeof patientImportRowDedupeStatusSchema
>;

export const patientImportRowResolutionSchema = z.enum([
  'SKIPPED',
  'REMOVED',
  'KEPT_OLD',
  'OVERWRITE',
  'KEEP_BOTH',
  'UPDATE_UPLOAD',
  'FIXED',
]);
export type PatientImportRowResolution = z.infer<
  typeof patientImportRowResolutionSchema
>;

export const patientImportRowCommitStatusSchema = z.enum([
  'COMMITTED_CREATE',
  'COMMITTED_UPDATE',
  'SKIPPED',
  'FAILED',
]);
export type PatientImportRowCommitStatus = z.infer<
  typeof patientImportRowCommitStatusSchema
>;

export const patientImportIssueCategorySchema = z.enum([
  'FORMAT_ERROR',
  'MISSING_DATA',
  'DUPE_IN_FILE',
  'DUPE_ID_EXACT_MATCH',
  'DUPE_ID_SAME_PATIENT',
  'DUPE_ID_DIFFERENT_PATIENT',
  'PATIENT_EXISTS_NO_ID_MATCH',
  'STALE_CONFLICT',
]);
export type PatientImportIssueCategory = z.infer<
  typeof patientImportIssueCategorySchema
>;

export const patientImportIssueSeveritySchema = z.enum([
  'ERROR',
  'WARN',
  'INFO',
]);
export type PatientImportIssueSeverity = z.infer<
  typeof patientImportIssueSeveritySchema
>;

// FE-selectable resolutions. FIXED is server-applied for inline-edit
// auto-resolution and FE-applied for the dupe-in-file pair survivor when
// the user removes the partner.
export const patientImportResolveKindSchema = z.enum([
  'SKIPPED',
  'REMOVED',
  'KEPT_OLD',
  'OVERWRITE',
  'KEEP_BOTH',
  'UPDATE_UPLOAD',
  'FIXED',
]);
export type PatientImportResolveKind = z.infer<
  typeof patientImportResolveKindSchema
>;

const patientImportJobSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  patientImportSourceId: z.string().uuid(),
  userUploadId: z.string().uuid(),
  filename: z.string(),
  status: patientImportJobStatusSchema,
  totalRows: z.number().int().nullable(),
  unknownHeaders: z.array(z.string()),
  missingRequiredHeaders: z.array(z.string()),
  committedCount: z.number().int(),
  failedCount: z.number().int(),
  skippedCount: z.number().int(),
  failureCategory: z.string().nullable(),
  failureMessage: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  finalizedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
});
export type PatientImportJob = z.infer<typeof patientImportJobSchema>;

const patientImportJobRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  patientImportJobId: z.string().uuid(),
  rowNumber: z.number().int(),
  rawData: z.record(z.unknown()),
  patientId: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  dedupeStatus: patientImportRowDedupeStatusSchema,
  matchedPatientId: z.string().uuid().nullable(),
  matchedExternalPatientsInfoId: z.string().uuid().nullable(),
  resolution: patientImportRowResolutionSchema.nullable(),
  commitStatus: patientImportRowCommitStatusSchema.nullable(),
  commitError: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PatientImportJobRow = z.infer<typeof patientImportJobRowSchema>;

const patientImportIssueSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  patientImportJobId: z.string().uuid(),
  patientImportJobRowId: z.string().uuid(),
  category: patientImportIssueCategorySchema,
  severity: patientImportIssueSeveritySchema,
  blocking: z.boolean(),
  field: z.string().nullable(),
  message: z.string(),
  metadata: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
});
export type PatientImportIssue = z.infer<typeof patientImportIssueSchema>;

export const patientImportEditableFieldSchema = z.enum([
  'patient_id',
  'first_name',
  'last_name',
  'date_of_birth',
  'email',
  'phone_number',
]);
export type PatientImportEditableField = z.infer<
  typeof patientImportEditableFieldSchema
>;

export const patientImportContract = c.router(
  {
    listImportSources: {
      method: 'GET',
      path: 'sources',
      responses: {
        200: z.object({
          knownSources: z.array(patientImportKnownSourceSchema),
          tenantSources: z.array(patientImportSourceSchema),
        }),
      },
      summary: 'List known and tenant-created patient import source systems',
    },

    resolveImportSource: {
      method: 'POST',
      path: 'sources/resolve',
      body: z.discriminatedUnion('type', [
        z.object({
          type: z.literal('KNOWN'),
          sourceKey: patientImportKnownSourceKeySchema,
        }),
        z.object({
          type: z.literal('CUSTOM'),
          displayName: z.string().trim().min(1).max(120),
        }),
      ]),
      responses: {
        200: z.object({
          sourceId: z.string().uuid(),
        }),
      },
      summary: 'Create or reuse a tenant patient import source system',
    },

    prepareUpload: {
      method: 'POST',
      path: 'prepare-upload',
      body: z.object({
        patientImportSourceId: z.string().uuid(),
        fileSize: z.number().int().positive(),
        fileName: z.string(),
        fileHash: z.string(),
      }),
      responses: {
        201: z.object({
          userUploadId: z.string().uuid(),
          uploadUrl: z.string().url(),
          gcsFilePath: z.string(),
        }),
      },
      summary: 'Prepare a GCS upload slot for a patient-import CSV',
    },

    startImport: {
      method: 'POST',
      path: '',
      body: z.object({
        patientImportSourceId: z.string().uuid(),
        userUploadId: z.string().uuid(),
        filename: z.string(),
      }),
      responses: {
        201: z.object({
          jobId: z.string().uuid(),
        }),
      },
      summary: 'Start a patient-import job (queues parse_patient_import)',
    },

    listImports: {
      method: 'GET',
      path: '',
      query: z.object({
        limit: z.coerce.number().int().positive().max(100).optional(),
        offset: z.coerce.number().int().nonnegative().optional(),
        orderBy: z
          .enum(['created_at_desc', 'created_at_asc', 'updated_at_desc'])
          .optional(),
      }),
      responses: {
        200: z.object({
          jobs: z.array(patientImportJobSchema),
          totalCount: z.number().int(),
        }),
      },
      summary: 'List patient-import jobs for the tenant',
    },

    getImport: {
      method: 'GET',
      path: ':jobId',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      responses: {
        200: z.object({
          job: patientImportJobSchema,
          counts: z.object({
            total: z.number().int(),
            ready: z.number().int(),
            blocking: z.number().int(),
            informational: z.number().int(),
          }),
        }),
      },
      summary: 'Get a patient-import job + summary counts',
    },

    listImportRows: {
      method: 'GET',
      path: ':jobId/rows',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      query: z.object({
        limit: z.coerce.number().int().positive().max(500).optional(),
        offset: z.coerce.number().int().nonnegative().optional(),
        dedupeStatus: patientImportRowDedupeStatusSchema.optional(),
        hasIssues: z.coerce.boolean().optional(),
        unresolved: z.coerce.boolean().optional(),
        search: z.string().optional(),
      }),
      responses: {
        200: z.object({
          rows: z.array(patientImportJobRowSchema),
          issuesByRowId: z.record(
            z.string(),
            z.array(patientImportIssueSchema),
          ),
          totalCount: z.number().int(),
        }),
      },
      summary: 'List patient-import rows for a job (paginated, filterable)',
    },

    listImportIssues: {
      method: 'GET',
      path: ':jobId/issues',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      query: z.object({
        category: patientImportIssueCategorySchema.optional(),
        limit: z.coerce.number().int().positive().max(500).optional(),
        offset: z.coerce.number().int().nonnegative().optional(),
      }),
      responses: {
        200: z.object({
          issues: z.array(patientImportIssueSchema),
          rowsByRowId: z.record(z.string(), patientImportJobRowSchema),
          totalCount: z.number().int(),
        }),
      },
      summary: 'List patient-import issues for a job (filter by category)',
    },

    updateImportRow: {
      method: 'PATCH',
      path: ':jobId/rows/:rowId',
      pathParams: z.object({
        jobId: z.string().uuid(),
        rowId: z.string().uuid(),
      }),
      body: z.object({
        field: patientImportEditableFieldSchema,
        value: z.string().nullable(),
      }),
      responses: {
        200: z.object({
          row: patientImportJobRowSchema,
          issues: z.array(patientImportIssueSchema),
        }),
      },
      summary: 'Edit a single field on a patient-import row + revalidate',
    },

    resolveImportRow: {
      method: 'POST',
      path: ':jobId/rows/:rowId/resolve',
      pathParams: z.object({
        jobId: z.string().uuid(),
        rowId: z.string().uuid(),
      }),
      body: z.object({
        kind: patientImportResolveKindSchema,
      }),
      responses: {
        200: z.object({
          row: patientImportJobRowSchema,
        }),
      },
      summary: 'Apply a user-chosen resolution to a patient-import row',
    },

    unresolveImportRow: {
      method: 'POST',
      path: ':jobId/rows/:rowId/unresolve',
      pathParams: z.object({
        jobId: z.string().uuid(),
        rowId: z.string().uuid(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          row: patientImportJobRowSchema,
        }),
      },
      summary: 'Clear a row resolution so the admin can re-decide',
    },

    resolveImportRowsBulk: {
      method: 'POST',
      path: ':jobId/rows/resolve-bulk',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      body: z.object({
        rowIds: z.array(z.string().uuid()).min(1).max(500),
        kind: patientImportResolveKindSchema,
      }),
      responses: {
        200: z.object({
          updatedCount: z.number().int(),
        }),
      },
      summary: 'Apply the same resolution to multiple rows at once',
    },

    finalizeImport: {
      method: 'POST',
      path: ':jobId/finalize',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          jobId: z.string().uuid(),
          status: patientImportJobStatusSchema,
        }),
        409: z.object({
          message: z.string(),
          blockingIssueCount: z.number().int(),
        }),
      },
      summary: 'Finalize an import (queues commit_patient_import worker)',
    },

    cancelImport: {
      method: 'POST',
      path: ':jobId/cancel',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          jobId: z.string().uuid(),
          status: patientImportJobStatusSchema,
        }),
      },
      summary: 'Cancel a patient-import job (parse or commit phase)',
    },

    downloadSkippedCsv: {
      method: 'GET',
      path: ':jobId/skipped.csv',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      responses: {
        200: z.string(),
      },
      summary: 'Download CSV of rows that were skipped at commit time',
    },

    downloadFailedCsv: {
      method: 'GET',
      path: ':jobId/failed.csv',
      pathParams: z.object({
        jobId: z.string().uuid(),
      }),
      responses: {
        200: z.string(),
      },
      summary: 'Download CSV of rows that failed at commit time',
    },
  },
  {
    pathPrefix: '/:tenantId/patient-imports/',
    pathParams: z.object({
      tenantId: z.string().uuid(),
    }),
  },
);
